<template>
    <v-dialog :value="value" @input="$emit('input', $event)" max-width="950px" persistent>
        <v-card class="rounded-xl overflow-hidden">
            <v-card-title class="headline d-flex align-center justify-space-between px-5 py-3"
                style="background: linear-gradient(135deg, #2563eb, #1d4ed8); border-bottom: 1px solid #1e40af;">
                <div class="d-flex align-center">
                    <v-icon color="white" size="24" class="mr-2">mdi-scale-balance</v-icon>
                    <span class="font-weight-black subtitle-1 white--text">Alteración y Ajuste Contable</span>
                </div>
                <v-btn icon color="white" @click="$emit('input', false)" small
                    style="background: rgba(255, 255, 255, 0.15);">
                    <v-icon small>mdi-close</v-icon>
                </v-btn>
            </v-card-title>

            <v-card-text class="pa-4 pt-4">
                <!-- Resumen del Presupuesto Seleccionado -->
                <v-row class="px-2 mb-2" dense>
                    <v-col cols="12" md="6">
                        <v-card flat outlined
                            class="pa-2 rounded-lg fill-height d-flex flex-column justify-center grey lighten-5"
                            style="border: 1px solid #e2e8f0;">
                            <span class="caption font-weight-bold text-uppercase tracking-wider grey--text mb-0">
                                Proyecto Asignado
                            </span>
                            <span class="subtitle-2 font-weight-black text-truncate primary--text">
                                {{ budget ? budget.PrjCode : '' }} -
                                {{ budget ? budget.PrjName : '' }}
                            </span>
                        </v-card>
                    </v-col>
                    <v-col cols="6" md="2">
                        <v-card flat outlined class="pa-2 rounded-lg fill-height d-flex flex-column justify-center"
                            style="border: 1px solid #e2e8f0; background: linear-gradient(135deg, #f8fafc, #f1f5f9);">
                            <span class="caption font-weight-bold text-uppercase tracking-wider grey--text mb-0">
                                Saldo Origen
                            </span>
                            <span class="subtitle-1 font-weight-black grey--text text--darken-3">
                                {{ formatMoney(getBudgetInitialBalance, budget?.BgtCurrencySymbol) }}
                            </span>
                        </v-card>
                    </v-col>
                    <v-col cols="6" md="2">
                        <v-card flat outlined class="pa-2 rounded-lg fill-height d-flex flex-column justify-center"
                            style="border: 1px solid #e2e8f0; background: linear-gradient(135deg, #ecfdf5, #d1fae5);">
                            <span
                                class="caption font-weight-bold text-uppercase tracking-wider green--text text--darken-2 mb-0">
                                Saldo Disponible Actual
                            </span>
                            <span class="subtitle-1 font-weight-black green--text text--darken-3">
                                {{ formatMoney(getBudgetAvailableBalance, budget?.BgtCurrencySymbol) }}

                                <span class="caption font-weight-bold grey--text text--darken-3"
                                    v-if="budget?.BgtCurrencyType == 2">
                                    ({{ formatMoney(budget?.BgtAvailableBalance) }})
                                </span>
                            </span>
                        </v-card>
                    </v-col>
                    <v-col cols="6" md="2">
                        <v-card flat outlined class="pa-2 rounded-lg fill-height d-flex flex-column justify-center"
                            style="border: 1px solid #e2e8f0; background: linear-gradient(135deg, #eff6ff, #dbeafe);">
                            <span
                                class="caption font-weight-bold text-uppercase tracking-wider blue--text text--darken-4 mb-0">
                                Tipo de Cambio
                            </span>
                            <span class="subtitle-1 font-weight-black blue--text text--darken-4">
                                {{ budget?.BgtRatePrice ? Number(budget.BgtRatePrice).toFixed(3) : '0.000' }}
                            </span>
                        </v-card>
                    </v-col>
                </v-row>

                <v-row dense>
                    <!-- Formulario de Nueva Alteración -->
                    <v-col cols="12" md="4" class="pr-md-2">
                        <v-card flat outlined class="pa-3 rounded-xl fill-height" style="border: 1px solid #e2e8f0;">
                            <div
                                class="subtitle-2 font-weight-black mb-3 grey--text text--darken-3 d-flex align-center">
                                <v-icon color="orange darken-2" size="20" class="mr-1">mdi-plus-circle-outline</v-icon>
                                Registrar Alteración
                            </div>

                            <v-form ref="formAlteration" lazy-validation>
                                <span class="caption font-weight-bold grey--text text--darken-1 mb-0 d-block">
                                    Fecha de Ajuste
                                </span>
                                <v-text-field v-model="alterationForm.BgtDate" type="date" outlined dense
                                    hide-details="auto" class="mb-2 rounded-lg" @input="onDateChange($event)">
                                </v-text-field>

                                <span class="caption font-weight-bold grey--text text--darken-1 d-block">
                                    Tipo de Alteración
                                </span>
                                <div class="ios-segmented-control mb-2">
                                    <div class="ios-slider" :class="{ 'slide-right': alterationForm.BgtType === '-' }">
                                    </div>
                                    <div class="ios-option option-plus"
                                        :class="{ 'active': alterationForm.BgtType === '+' }"
                                        @click="alterationForm.BgtType = '+'">
                                        INCREMENTAR
                                    </div>
                                    <div class="ios-option option-minus"
                                        :class="{ 'active': alterationForm.BgtType === '-' }"
                                        @click="alterationForm.BgtType = '-'">
                                        DISMINUIR
                                    </div>
                                </div>

                                <span class="caption font-weight-bold grey--text text--darken-1 d-block">
                                    Tipo de Moneda
                                </span>
                                <div class="ios-segmented-control mb-2">
                                    <div class="ios-slider"
                                        :class="{ 'slide-right': alterationForm.BgtCurrencyType === 2 }">
                                    </div>
                                    <div class="ios-option option-currency"
                                        :class="{ 'active': alterationForm.BgtCurrencyType === 1 }"
                                        @click="onCurrencyChange(1)">
                                        SOLES
                                    </div>
                                    <div class="ios-option option-currency"
                                        :class="{ 'active': alterationForm.BgtCurrencyType === 2 }"
                                        @click="onCurrencyChange(2)">
                                        DOLARES
                                    </div>
                                </div>

                                <span class="caption font-weight-bold grey--text text--darken-1 mb-0 d-block">
                                    Monto de Ajuste
                                </span>
                                <v-text-field v-model.number="alterationForm.BgtAmount" type="number" min="0.01"
                                    step="0.01" outlined dense hide-details="auto" class="mb-2 rounded-lg"
                                    :prefix="alterationForm.BgtCurrencyType === 1 ? 'S/.' : '$'">
                                </v-text-field>

                                <!-- Conversión automática a Soles -->
                                <div v-if="alterationForm.BgtCurrencyType == 2 && alterationForm.BgtAmount > 0 && budget?.BgtRatePrice > 0"
                                    class="conversion-preview mb-2">
                                    <v-icon size="12" color="#10b981" class="mr-1">mdi-swap-horizontal</v-icon>
                                    <span class="conversion-value">
                                        S/.
                                        {{ (alterationForm.BgtAmount *
                                            budget.BgtRatePrice).toFixed(3).replace(/\d(?=(\d{3})+\.)/g, '$&,') }}
                                    </span>
                                </div>



                                <span class="caption font-weight-bold grey--text text--darken-1 mb-0 d-block">
                                    Justificación / Motivo
                                </span>
                                <v-textarea v-model="alterationForm.BgtJustification" outlined dense rows="2"
                                    placeholder="Describa el motivo detallado de esta variación contable..."
                                    hide-details="auto" class="mb-3 rounded-lg"></v-textarea>

                                <v-btn block color="primary" depressed @click="save()"
                                    class="font-weight-black py-3 rounded-lg text-none elevation-0 subtitle-2"
                                    style="background: linear-gradient(135deg, #2563eb, #1d4ed8) !important; color: white;">
                                    GUARDAR
                                </v-btn>
                            </v-form>
                        </v-card>
                    </v-col>

                    <!-- Historial de Alteraciones (Línea de Tiempo) -->
                    <v-col cols="12" md="8" class="pl-md-2">
                        <v-card flat outlined class="pa-3 rounded-xl fill-height" style="border: 1px solid #e2e8f0;">
                            <div
                                class="subtitle-2 font-weight-black mb-3 grey--text text--darken-3 d-flex align-center">
                                <v-icon color="blue" size="20" class="mr-1">mdi-history</v-icon>
                                Línea de Tiempo de Variaciones
                            </div>

                            <div style="max-height: 350px; overflow-y: auto; padding-right: 4px;">
                                <v-timeline dense align-top class="pt-0">
                                    <v-timeline-item v-for="(alt, index) in historyRecords" :key="index"
                                        :color="alt.BgtType === '+' ? 'green darken-2' : 'red darken-2'" small fill-dot>
                                        <template v-slot:icon>
                                            <v-icon size="12" color="white">
                                                {{ alt.BgtType === '+' ? 'mdi-trending-up' : 'mdi-trending-down' }}
                                            </v-icon>
                                        </template>

                                        <v-card flat class="rounded-lg border pa-2 mb-2"
                                            style="border: 1px solid #f1f5f9; background: #fafafa;">
                                            <div class="d-flex justify-space-between align-center mb-1">
                                                <span class="caption font-weight-black text-uppercase tracking-wider"
                                                    :class="alt.BgtType === '+' ? 'green--text text--darken-2' : 'red--text text--darken-2'">
                                                    {{ alt.BgtType === '+' ? 'Incremento' : 'Reducción' }}
                                                </span>
                                                <span class="caption grey--text font-weight-medium">
                                                    {{ $fun.formatDateView(alt.BgtDate) }}
                                                </span>
                                            </div>

                                            <div class="d-flex align-center justify-space-between mt-1">
                                                <div>
                                                    <!-- <span class="caption grey--text">Origen: </span>
                                                    <span class="caption font-weight-bold grey--text text--darken-3">
                                                        {{ formatMoney(alt.initialBalance) }}
                                                    </span> -->
                                                </div>
                                                <div v-if="alt.BgtCurrencyAbbreviation == 'USD'">
                                                    <span class="caption grey--text">T.C.: </span>
                                                    <span class="caption font-weight-bold grey--text text--darken-3">
                                                        {{ alt.BgtRatePrice.toFixed(3) }}
                                                    </span>
                                                </div>
                                                <div>
                                                    <span class="caption grey--text">Ajuste: </span>
                                                    <span class="caption font-weight-black"
                                                        :class="alt.BgtType === '+' ? 'green--text text--darken-2' : 'red--text text--darken-2'">
                                                        {{ alt.BgtType == '+' ? '+' : '-' }}
                                                        {{
                                                            formatMoney(getAmountAlteration(alt), alt.BgtCurrencySymbol)
                                                        }}
                                                    </span>

                                                    <!-- SI PRESUPUESTO ES USD Y ALTERACION ES USD -->
                                                    <v-chip x-small rounded outlined
                                                        v-if="budget.BgtCurrencyType == 2 && alt.BgtCurrencyType == 2"
                                                        class="caption font-weight-bold grey--text text--darken-3 rounded pa-2 px-1">
                                                        {{ formatMoney(alt.BgtAmount) }}
                                                    </v-chip>

                                                    <!-- SI PRESUPUESTO ES SOLES Y ALTERACION ES DOLARES -->
                                                    <v-chip x-small rounded outlined
                                                        v-if="budget.BgtCurrencyType == 1 && alt.BgtCurrencyType == 2"
                                                        class="caption font-weight-bold grey--text text--darken-3 rounded pa-2 px-1">
                                                        {{ formatMoney(alt.BgtAmount) }}
                                                    </v-chip>
                                                </div>
                                            </div>

                                            <div class="d-flex align-center justify-space-between mt-1 pt-1"
                                                style="border-top: 1px dashed #e2e8f0;">
                                                <!-- <span class="caption grey--text font-weight-medium">Saldo Final:</span>
                                                <span class="caption-2 font-weight-black black--text">
                                                    {{ formatMoney(alt.finalBalance) }}
                                                </span> -->
                                            </div>

                                            <div class="caption grey--text text--darken-2 mt-1 pa-1 rounded white font-italic"
                                                style="border-left: 3px solid #cbd5e1; background-color: #ffffff;">
                                                "{{ alt.BgtJustification }}"
                                            </div>
                                        </v-card>
                                    </v-timeline-item>
                                </v-timeline>

                                <div v-if="historyRecords.length === 0"
                                    class="d-flex flex-column align-center justify-center py-6 grey--text text-center">
                                    <v-icon size="36" color="grey lighten-2"
                                        class="mb-1">mdi-clipboard-text-play-outline</v-icon>
                                    <span class="caption font-weight-medium">
                                        No se registran alteraciones para este presupuesto.
                                    </span>
                                </div>
                            </div>
                        </v-card>
                    </v-col>
                </v-row>
            </v-card-text>
        </v-card>
    </v-dialog>
</template>

<script>

import TrsBudgetAlterationService from "@/services/Treasury/TrsBudgetAlteration";
import _sOtherSupportService from "@/services/Security/OtherSupportService";

export default {
    props: {
        value: {
            type: Boolean,
            default: false
        },
        budget: {
            type: Object,
            default: null
        }
    },
    data() {
        return {
            historyRecords: [],
            tipoCambio: null,
            alterationForm: {
                BgthID: 0,
                BgtType: "+",
                BgtCurrencyType: 1,
                BgtAmount: 0.00,
                BgtAmountUSD: 0.00,
                BgtRatePrice: null,
                BgtJustification: "",
                BgtDate: new Date().toISOString().substr(0, 10),
                BgtID: this.budget ? this.budget.BgtID : null,
                UsrCreateID: this.$fun.getUserID(),
                SecStatus: 1
            }
        }
    },
    async created() {
        if (this.budget && this.budget.BgtRatePrice) {
            this.tipoCambio = this.budget.BgtRatePrice;
            this.alterationForm.BgtRatePrice = this.budget.BgtRatePrice;
        } else {
            const rate = await this.getExchangeRate(new Date(), "US$");
            if (rate) this.tipoCambio = rate;
            if (rate) this.alterationForm.BgtRatePrice = rate;
        }
        this.initializeMockData();
        if (this.budget) {
            this.alterationForm.BgtID = this.budget.BgtID;
            this.alterationForm.BgtCurrencyType = this.budget.BgtCurrencyType;
        }
    },
    watch: {
        budget: {
            handler() {
                this.initializeMockData();
                if (this.budget) {
                    this.alterationForm.BgtID = this.budget.BgtID;
                    if (this.budget.BgtRatePrice) {
                        this.tipoCambio = this.budget.BgtRatePrice;
                        this.alterationForm.BgtRatePrice = this.budget.BgtRatePrice;
                    }
                }
            },
            deep: true
        }
    },
    computed: {
        getBudgetInitialBalance() {
            if (!this.budget) return 0.00;
            return this.budget.BgtCurrencyType == 2 ? this.budget.BgtInitialBalanceUSD : this.budget.BgtInitialBalance;
        },
        getBudgetAvailableBalance() {
            if (!this.budget) return 0.00;
            const rate = Number(this.budget.BgtRatePrice) || 1;
            return this.budget.BgtCurrencyType == 2 ? (this.budget.BgtAvailableBalance / rate) : this.budget.BgtAvailableBalance;
        },
    },
    methods: {
        getAmountAlteration(item) {
            return item.BgtCurrencyType == 2 ? item.BgtAmountUSD : item.BgtAmount;
        },
        async getExchangeRate(date, currencyType) {
            try {
                const result = await _sOtherSupportService.exchangeratesap(date, currencyType, this.$fun.getUserID());
                if (result.status == 200 && result.data && result.data.Rate) {
                    return result.data.Rate;
                }
                return null;
            } catch (error) {
                console.log("Error al obtener la tasa de cambio:", error);
                return null;
            }
        },
        async onDateChange(date) {
            // Se utiliza el tipo de cambio predefinido del presupuesto
            if (this.budget && this.budget.BgtRatePrice) {
                this.tipoCambio = this.budget.BgtRatePrice;
                this.alterationForm.BgtRatePrice = this.budget.BgtRatePrice;
            }
        },
        onCurrencyChange(type) {
            this.alterationForm.BgtCurrencyType = type;
        },
        async getPaginationBudgetAlterationAsList() {
            const params = {
                filter: { BgtID: this.budget.BgtID }, draw: 1, start: 0, length: -1,
                order: [{ dir: "desc", column: "0" }], searchText: "",
            };
            const response = await TrsBudgetAlterationService.pagination(params, this.$fun.getUserID());
            return response.status == 200 ? response.data.data : [];
        },

        async initializeMockData() {
            if (!this.budget) return;
            const dbRecords = await this.getPaginationBudgetAlterationAsList();
            let tempBalance = Number(this.budget.BgtInitialBalance) || 0;

            this.historyRecords = dbRecords.map(rec => {
                const amount = Number(rec.BgtAmount) || 0;
                const isIncrease = rec.BgtType === "+";
                const initialBalance = tempBalance;
                const finalBalance = isIncrease ? tempBalance + amount : tempBalance - amount;
                tempBalance = finalBalance;

                return {
                    ...rec,
                    initialBalance: initialBalance,
                    amount: amount,
                    finalBalance: finalBalance
                };
            });
        },
        save() {
            if (!this.alterationForm.BgtAmount || this.alterationForm.BgtAmount <= 0) {
                this.$fun.alert("Debe ingresar un monto válido mayor a 0", "warning");
                return;
            }

            if (!this.alterationForm.BgtCurrencyType || this.alterationForm.BgtCurrencyType == 0) {
                this.$fun.alert("Debe seleccionar un tipo de moneda", "warning");
                return;
            }

            // Forzar el tipo de cambio del presupuesto actual
            if (this.budget && this.budget.BgtRatePrice) {
                this.alterationForm.BgtRatePrice = this.budget.BgtRatePrice;
            }

            if (!this.alterationForm.BgtRatePrice || this.alterationForm.BgtRatePrice <= 0) {
                this.$fun.alert("El presupuesto no tiene un tipo de cambio válido asignado", "warning");
                return;
            }

            if (!this.alterationForm.BgtJustification || this.alterationForm.BgtJustification.trim() === "") {
                this.$fun.alert("Debe ingresar una justificación o motivo", "warning");
                return;
            }

            // — Validación de saldo al REDUCIR —
            if (this.alterationForm.BgtType == '-' && this.budget) {
                const saldoDisponible = parseFloat(this.budget.BgtAvailableBalance) || 0;
                const rate = parseFloat(this.budget.BgtRatePrice) || 1;
                const montoSoles = this.alterationForm.BgtCurrencyType == 2
                    ? parseFloat((this.alterationForm.BgtAmount * rate).toFixed(3))
                    : parseFloat(this.alterationForm.BgtAmount) || 0;

                if (montoSoles > saldoDisponible) {
                    this.$fun.alert(
                        `La reducción solicitada (S/. ${montoSoles.toFixed(3)}) supera el saldo contable actual.\n` +
                        `Saldo actual: S/. ${saldoDisponible.toFixed(3)}\n` +
                        `El presupuesto no puede quedar negativo.`,
                        "warning"
                    );
                    return;
                }
            }

            this.$fun.alert("¿Seguro de guardar?", "question").then((r) => {
                this.alterationForm.UsrCreateID = this.$fun.getUserID();
                let payload = { ...this.alterationForm }

                if (this.alterationForm.BgtCurrencyType == 2) {
                    payload.BgtAmountUSD = this.alterationForm.BgtAmount;
                    payload.BgtAmount = (this.alterationForm.BgtAmount * this.alterationForm.BgtRatePrice).toFixed(3);
                } else {
                    payload.BgtAmountUSD = (this.alterationForm.BgtAmount / this.alterationForm.BgtRatePrice).toFixed(3);
                }

                if (r.value) {
                    TrsBudgetAlterationService.save(payload, this.$fun.getUserID()).then((response) => {
                        if (response.status == 200) {
                            this.$emit('update-budget', this.alterationForm);
                            this.$fun.alert("Registrado correctamente", "success");
                            this.resetForm(this.alterationForm.BgtID);
                            this.initializeMockData();
                        } else {
                            this.$fun.alert("Error al registrar", "error");
                        }
                    }).catch((error) => {
                        console.log("Error al guardar alteración en BD:", error);
                        this.$fun.alert("Error al registrar " + error.message, "error");
                    });
                }
            });
        },
        delete(item) {
            this.$fun.alert("¿Seguro de eliminar?", "question").then((r) => {
                if (r.value) {
                    const payload = {
                        ...item,
                        SecStatus: 0,
                        UsrCreateID: this.$fun.getUserID()
                    }

                    TrsBudgetAlterationService.save(payload, this.$fun.getUserID()).then((response) => {
                        if (response.status == 200) {
                            console.log(JSON.parse(JSON.stringify({ response })))
                            this.$emit('update-budget', payload);
                            this.$fun.alert("Eliminado correctamente", "success");
                            this.resetForm(payload.BgtID);
                            this.initializeMockData();
                        } else {
                            this.$fun.alert("Error al eliminar", "error");
                        }
                    }).catch((error) => {
                        console.log("Error al eliminar alteración en BD:", error);
                        this.$fun.alert("Error al eliminar " + error.message, "error");
                    });
                }
            });
        },
        resetForm(bgtId) {
            if (this.budget && this.budget.BgtRatePrice) {
                this.tipoCambio = this.budget.BgtRatePrice;
            }
            this.alterationForm = {
                BgthID: 0,
                BgtType: "+",
                BgtCurrencyType: 1,
                BgtAmount: 0.00,
                BgtRatePrice: this.tipoCambio,
                BgtAmountUSD: 0.00,
                BgtJustification: "",
                BgtDate: new Date().toISOString().substr(0, 10),
                BgtID: bgtId,
                UsrCreateID: this.$fun.getUserID(),
                SecStatus: 1
            };
        },
        formatMoney(value, simbolo) {
            const sym = simbolo || 'S/.';
            if (value == null || isNaN(value)) return sym + " 0.00";
            return sym + Number(value).toFixed(3).replace(/\d(?=(\d{3})+\.)/g, '$&,');
        }
    }
}
</script>

<style scoped>
/* iOS Segmented Control Switch */
.ios-segmented-control {
    position: relative;
    display: flex;
    background-color: #f1f5f9;
    border-radius: 12px;
    padding: 4px;
    user-select: none;
    border: 1px solid #e2e8f0;
}

.ios-slider {
    position: absolute;
    top: 4px;
    left: 4px;
    width: calc(50% - 4px);
    height: calc(100% - 8px);
    background-color: #ffffff;
    border-radius: 9px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.04);
    transition: transform 0.3s cubic-bezier(0.25, 1, 0.5, 1);
}

.ios-slider.slide-right {
    transform: translateX(100%);
}

.ios-option {
    position: relative;
    z-index: 1;
    flex: 1;
    text-align: center;
    padding: 8px 0;
    font-size: 11px;
    font-weight: 800;
    color: #94a3b8;
    cursor: pointer;
    transition: color 0.25s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
    letter-spacing: 0.5px;
}

.ios-option.active.option-plus {
    color: #047857;
}

.ios-option.active.option-minus {
    color: #b91c1c;
}

.ios-option.active.option-currency {
    color: #1e293b;
}

/* Conversión automática a Soles */
.conversion-preview {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 5px 10px;
    background: linear-gradient(135deg, #ecfdf5, #d1fae5);
    border: 1px solid #6ee7b7;
    border-radius: 7px;
    font-size: 11px;
}

.conversion-value {
    color: #064e3b;
    font-weight: 800;
    font-family: Consolas, Monaco, monospace;
    margin-left: 2px;
}
</style>
